"use client";

import Image from "next/image";
import Link from "next/link";
import { useMemo, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  Check,
  LifeBuoy,
  Radio,
  ShoppingBag,
  Usb,
} from "lucide-react";

import Footer from "@/components/footer";
import Navbar from "@/components/navbar";
import { DONGL_PRELAUNCH } from "@/components/store/dongl-config";
import type { StoreProduct, StoreVariant } from "@/components/store/types";
import { useCart } from "@/context/cart-context";

type DonglLandingProps = Readonly<{
  products: readonly StoreProduct[];
}>;

const DONGL_SITE_URL =
  "https://simpleble.org/dongl?utm_source=simpleble_store&utm_medium=store&utm_campaign=dongl_catalog";

const getVariantLabel = (variant: StoreVariant): string =>
  [variant.colorName, variant.sizeName].filter(Boolean).join(" / ") ||
  "Standard";

const DonglRadioField = (): React.ReactElement => (
  <div
    className="relative mx-auto h-[210px] w-full max-w-[520px] overflow-hidden sm:h-[280px]"
    aria-hidden="true"
  >
    <div className="dongl-radio-grid absolute inset-0" />
    <div className="absolute top-1/2 left-1/2 h-px w-[72%] -translate-x-1/2 bg-gradient-to-r from-transparent via-[#4099ff]/45 to-transparent" />
    <div className="absolute top-1/2 left-1/2 h-[72%] w-px -translate-y-1/2 bg-gradient-to-b from-transparent via-[#4099ff]/30 to-transparent" />

    <div className="absolute top-1/2 left-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 sm:h-52 sm:w-52">
      <span className="dongl-radio-ring absolute inset-0 rounded-full border border-[#58a9ff]/45" />
      <span className="dongl-radio-ring dongl-radio-ring-delay-1 absolute inset-0 rounded-full border border-[#58a9ff]/40" />
      <span className="dongl-radio-ring dongl-radio-ring-delay-2 absolute inset-0 rounded-full border border-[#58a9ff]/35" />
    </div>

    <div className="absolute top-1/2 left-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-[#0d3158] shadow-[0_0_55px_rgba(33,127,241,0.65)] sm:h-20 sm:w-20">
      <Radio className="h-7 w-7 text-white sm:h-8 sm:w-8" />
    </div>
  </div>
);

const EmptyCatalog = (): React.ReactElement => (
  <div className="rounded-3xl border border-[#cbd9e8] bg-white p-8 text-center shadow-[0_22px_65px_rgba(8,26,44,0.07)] sm:p-12">
    <div className="text-primary mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#eaf3fc]">
      <Radio className="h-6 w-6" aria-hidden="true" />
    </div>
    <h2 className="font-sora mt-6 text-2xl font-semibold tracking-[-0.03em] text-[#081a2c]">
      No Dongl products are available
    </h2>
    <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-[#5b6f82] sm:text-base">
      Visit the Dongl page for product and firmware updates.
    </p>
    <Link
      href={DONGL_SITE_URL}
      className="font-sora border-primary text-primary hover:bg-primary/5 mt-7 inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border px-5 text-sm font-semibold transition-colors"
    >
      Learn about Dongl
      <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </Link>
  </div>
);

const DonglProductCard = ({
  product,
}: Readonly<{ product: StoreProduct }>): React.ReactElement => {
  const { addToCart } = useCart();
  const firstAvailableVariant = useMemo(
    () =>
      product.variants.find((variant) => variant.isAvailable) ??
      product.variants[0],
    [product.variants],
  );
  const [selectedVariantId, setSelectedVariantId] = useState(
    firstAvailableVariant?.id ?? "",
  );
  const selectedVariant =
    product.variants.find((variant) => variant.id === selectedVariantId) ??
    firstAvailableVariant;
  const selectedImages = selectedVariant?.colorName
    ? product.imagesByColor[selectedVariant.colorName]
    : undefined;
  const image = selectedImages?.[0] ?? product.images[0];
  const canAddToCart = Boolean(
    selectedVariant?.isAvailable && !product.isSoldOut,
  );

  return (
    <article className="grid min-w-0 overflow-hidden rounded-3xl border border-[#cbd9e8] bg-white shadow-[0_24px_70px_rgba(8,26,44,0.08)] lg:grid-cols-[1.05fr_0.95fr]">
      <Link
        href={`/products/${product.slug}`}
        className="group relative min-h-[340px] overflow-hidden bg-[#eaf3fc] sm:min-h-[480px] lg:min-h-[570px]"
        aria-label={`View ${product.name}`}
      >
        <div className="absolute inset-0 [background-image:linear-gradient(rgba(33,127,241,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(33,127,241,.08)_1px,transparent_1px)] [background-size:40px_40px]" />
        {image ? (
          <Image
            src={image.transformedUrl ?? image.url}
            alt={product.name}
            fill
            priority
            sizes="(max-width: 1024px) 100vw, 52vw"
            className="object-cover transition-transform duration-500 group-hover:scale-[1.015]"
          />
        ) : (
          <div className="relative flex h-full items-center justify-center">
            <Image
              src="/images/logos/logomark.png"
              alt=""
              width={96}
              height={96}
              className="opacity-80"
            />
          </div>
        )}
      </Link>

      <div className="flex min-w-0 flex-col p-6 sm:p-9 lg:p-11">
        <p className="flex items-center gap-2 self-end text-xs font-semibold text-[#3c5b4b]">
          <span
            className={`h-2 w-2 rounded-full ${canAddToCart ? "bg-[#2da66f]" : "bg-[#cf8b2d]"}`}
            aria-hidden="true"
          />
          {canAddToCart
            ? "Available"
            : DONGL_PRELAUNCH
              ? "Coming soon"
              : "Unavailable"}
        </p>

        <h2 className="font-sora mt-3 text-3xl leading-tight font-semibold tracking-[-0.045em] text-[#081a2c] sm:text-4xl">
          {product.name}
        </h2>
        {product.description && (
          <p className="mt-5 text-base leading-7 text-[#5b6f82]">
            {product.description}
          </p>
        )}

        <div className="my-7 h-px bg-[#d9e4ee]" />

        <p className="font-sora text-2xl font-semibold tracking-[-0.03em] text-[#081a2c]">
          {selectedVariant?.priceLabel ??
            product.priceLabel ??
            "Price unavailable"}
          {selectedVariant?.compareAtPriceLabel && (
            <s className="ml-2.5 text-base font-medium text-[#8ba0b4]">
              {selectedVariant.compareAtPriceLabel}
            </s>
          )}
        </p>

        {product.variants.length > 1 && (
          <fieldset className="mt-6">
            <legend className="font-sora mb-3 text-xs font-semibold text-[#334b62]">
              Configuration
            </legend>
            <div className="flex flex-wrap gap-2">
              {product.variants.map((variant) => {
                const isSelected = variant.id === selectedVariant?.id;

                return (
                  <button
                    key={variant.id}
                    type="button"
                    disabled={!variant.isAvailable}
                    onClick={() => setSelectedVariantId(variant.id)}
                    aria-pressed={isSelected}
                    className={`font-sora min-h-11 cursor-pointer rounded-xl border px-4 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                      isSelected
                        ? "border-primary bg-primary/5 text-primary"
                        : "hover:border-primary/50 border-[#cbd9e8] text-[#334b62]"
                    }`}
                  >
                    {isSelected && (
                      <Check className="mr-1.5 inline h-3.5 w-3.5" />
                    )}
                    {getVariantLabel(variant)}
                  </button>
                );
              })}
            </div>
          </fieldset>
        )}

        <div className="mt-auto grid gap-3 pt-8 sm:grid-cols-[1fr_auto]">
          <button
            type="button"
            disabled={!canAddToCart}
            onClick={() => {
              if (selectedVariant && canAddToCart) {
                addToCart(product, { variant: selectedVariant, quantity: 1 });
              }
            }}
            className="font-sora bg-primary inline-flex min-h-14 cursor-pointer items-center justify-center gap-2 rounded-xl px-6 text-sm font-semibold text-white shadow-[0_12px_28px_rgba(33,127,241,0.22)] transition-colors hover:bg-[#176fd5] disabled:cursor-not-allowed disabled:bg-[#d7e0e9] disabled:text-[#718293] disabled:shadow-none"
          >
            <ShoppingBag className="h-4.5 w-4.5" aria-hidden="true" />
            {product.isSoldOut
              ? DONGL_PRELAUNCH
                ? "Coming soon"
                : "Sold out"
              : canAddToCart
                ? "Add to cart"
                : "Unavailable"}
          </button>
          <Link
            href={`/products/${product.slug}`}
            className="font-sora hover:border-primary/40 hover:text-primary inline-flex min-h-14 items-center justify-center rounded-xl border border-[#cbd9e8] px-5 text-sm font-semibold text-[#334b62] transition-colors"
          >
            Details
          </Link>
        </div>
      </div>
    </article>
  );
};

export const DonglLanding = ({
  products,
}: DonglLandingProps): React.ReactElement => (
  <>
    <Navbar variant="dark" />
    <main className="min-h-screen bg-[#f4f8fc] text-[#081a2c]">
      <section className="relative overflow-hidden bg-[#061528] px-4 pt-28 pb-12 text-white sm:pt-32 sm:pb-16 md:px-16 lg:pt-36 lg:pb-18">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_76%_48%,rgba(33,127,241,0.22),transparent_34%)]" />
        <div className="relative mx-auto grid max-w-[1312px] items-center gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          <div className="max-w-2xl">
            <h1 className="font-sora text-4xl leading-tight font-semibold tracking-[-0.05em] sm:text-5xl lg:text-6xl">
              SimpleBLE Dongl
            </h1>
            <p className="mt-5 max-w-xl text-base leading-7 text-white/70 sm:text-lg sm:leading-8">
              The official hardware and firmware companion to SimpleBLE.
            </p>
            <Link
              href={DONGL_SITE_URL}
              className="font-sora mt-6 inline-flex min-h-12 items-center gap-2 rounded-xl border border-white/25 bg-white/[0.06] px-5 text-sm font-semibold text-white transition-colors hover:bg-white/[0.12]"
            >
              Learn about Dongl
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
          <DonglRadioField />
        </div>
      </section>

      <section className="px-4 py-10 sm:py-14 md:px-16 lg:py-16">
        <div className="mx-auto max-w-[1312px]">
          <h2 className="sr-only">Dongl products</h2>

          <div className="space-y-8">
            {products.length > 0 ? (
              products.map((product) => (
                <DonglProductCard key={product.id} product={product} />
              ))
            ) : (
              <EmptyCatalog />
            )}
          </div>

          {products.length > 0 && (
            <div className="mt-8 grid gap-px overflow-hidden rounded-2xl border border-[#cbd9e8] bg-[#cbd9e8] sm:grid-cols-3">
              <div className="flex items-center justify-center gap-3 bg-white px-5 py-4 text-sm text-[#506579]">
                <Usb className="text-primary h-4 w-4 shrink-0" />
                Plug-and-play ready
              </div>
              <div className="flex items-center justify-center gap-3 bg-white px-5 py-4 text-sm text-[#506579]">
                <BadgeCheck className="text-primary h-4 w-4 shrink-0" />
                Commercial license included
              </div>
              <div className="flex items-center justify-center gap-3 bg-white px-5 py-4 text-sm text-[#506579]">
                <LifeBuoy className="text-primary h-4 w-4 shrink-0" />
                Direct expert support
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
    <Footer />
  </>
);
